/**
 * Host + token resolution for QCC credit pay().
 *
 * Unknown hosts are MAINNET. Only localhost and a DNS label of `dev`,
 * `test`, or `staging` select testnet. The previous allowlist
 * (`host !== 'quickcam.app'`) sent pay() at the testnet token on every
 * other production-like host while MetaMask stayed on BSC mainnet.
 */

export type QccUiNetwork =
    | 'Base'
    | 'BNB Chain'
    | 'Polygon'
    | 'Optimism'
    | 'Arbitrum'
    | 'Avalanche';

export const QCAM_TOKEN_BNB_MAINNET = '0x5e0004a3912cac14db8697145912ceda6f7c5771';
export const QCAM_TOKEN_BNB_TESTNET = '0x9082fc835ae29aee1c0ddac73604d832a9d68189';

export interface QccPayTarget {
    chainId: number;
    tokenAddress: string;
}

const TESTNET_LABELS = new Set(['dev', 'test', 'staging']);

const PAY_TARGETS: Record<QccUiNetwork, { mainnet: QccPayTarget; testnet: QccPayTarget }> = {
    Base: {
        mainnet: { chainId: 8453, tokenAddress: '0x4e96d27c2f1b8098349952784d6fecb52b669321' },
        testnet: { chainId: 84532, tokenAddress: '0xc6fe3521644e0f5439fe345f64d372cd6ed37491' },
    },
    'BNB Chain': {
        mainnet: { chainId: 56, tokenAddress: QCAM_TOKEN_BNB_MAINNET },
        testnet: { chainId: 97, tokenAddress: QCAM_TOKEN_BNB_TESTNET },
    },
    Polygon: {
        mainnet: { chainId: 137, tokenAddress: '0x5e0004a3912cac14db8697145912ceda6f7c5771' },
        testnet: { chainId: 80002, tokenAddress: '0x2defa39e7dfa5c8532fe0bda761571bf15d4623b' },
    },
    Optimism: {
        mainnet: { chainId: 10, tokenAddress: '0x5e0004a3912cac14db8697145912ceda6f7c5771' },
        testnet: { chainId: 11155420, tokenAddress: '0x76a3dce26437bad35031207b9162c4951ec8cf47' },
    },
    Arbitrum: {
        mainnet: { chainId: 42161, tokenAddress: '0x5e0004a3912cac14db8697145912ceda6f7c5771' },
        testnet: { chainId: 421614, tokenAddress: '0x76a3dce26437bad35031207b9162c4951ec8cf47' },
    },
    Avalanche: {
        mainnet: { chainId: 43114, tokenAddress: '0x5e0004a3912cac14db8697145912ceda6f7c5771' },
        testnet: { chainId: 43113, tokenAddress: '0x76A3dCe26437bad35031207B9162c4951EC8cf47' },
    },
};

export function isQccTestnetHost(hostname: string): boolean {
    const host = (hostname || '').toLowerCase();
    if (host === 'localhost' || host.endsWith('.localhost') || host === '127.0.0.1') {
        return true;
    }
    return host.split('.').some((label) => TESTNET_LABELS.has(label));
}

export function getQccPayTarget(
    network: QccUiNetwork,
    hostname: string,
): QccPayTarget {
    const entry = PAY_TARGETS[network];
    return isQccTestnetHost(hostname) ? entry.testnet : entry.mainnet;
}

export function walletMatchesPayChain(walletChainIdHex: string, expectedChainId: number): boolean {
    const walletChainId = Number.parseInt(walletChainIdHex, 16);
    return walletChainId === expectedChainId;
}
